import { useState } from 'react';
import { Text, Collapse, Button } from '@mantine/core';
import ReactMarkdown from 'react-markdown';
import { IconBrain, IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import type { AIMessage } from '~/common/vm/protocol';

export function AIChainOfThoughtText({ message }: { message: Extract<AIMessage, { type: 'chain-of-thought' }> }) {
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <div className="text-sm text-gray-600 dark:text-gray-400">
      <Button
        onClick={() => setIsExpanded(!isExpanded)}
        variant="subtle"
        size="xs"
        color="gray"
        justify="flex-start"
        fullWidth
        leftSection={
          <div className="flex items-center">
            <IconBrain
              size={12}
              className="group-hover:hidden"
            />
            {isExpanded ?
              <IconChevronDown
                size={12}
                className="hidden group-hover:block"
              />
            : <IconChevronRight
                size={12}
                className="hidden group-hover:block"
              />
            }
          </div>
        }
        className="group -ml-1 -mt-1 h-auto p-0 font-normal hover:bg-transparent hover:text-gray-700 dark:hover:bg-transparent dark:hover:text-gray-200 [&>*]:gap-0.5"
      >
        Thought
      </Button>

      <Collapse in={isExpanded}>
        <div style={{ maxHeight: 200, overflowY: 'auto' }}>
        <ReactMarkdown
          components={{
            // oxlint-disable-next-line react/no-unstable-nested-components
            p: ({ children }) => (
              <Text
                size="sm"
                className="mb-2 last:mb-0"
              >
                {children}
              </Text>
            ),
            // oxlint-disable-next-line react/no-unstable-nested-components
            strong: ({ children }) => (
              <Text
                component="span"
                size="sm"
                fw={600}
              >
                {children}
              </Text>
            ),
            // oxlint-disable-next-line react/no-unstable-nested-components
            em: ({ children }) => (
              <Text
                component="span"
                size="sm"
                fs="italic"
              >
                {children}
              </Text>
            ),
            // oxlint-disable-next-line react/no-unstable-nested-components
            code: ({ children }) => (
              <Text
                component="code"
                size="sm"
                className="rounded bg-gray-100 px-1 py-0.5 dark:bg-gray-800"
              >
                {children}
              </Text>
            ),
            // oxlint-disable-next-line react/no-unstable-nested-components
            pre: ({ children }) => (
              <Text
                component="pre"
                size="sm"
                className="mb-2 mt-1 overflow-x-auto rounded bg-gray-100 p-2 dark:bg-gray-800"
              >
                {children}
              </Text>
            ),
            // oxlint-disable-next-line react/no-unstable-nested-components
            ul: ({ children }) => (
              <Text
                component="ul"
                size="sm"
                className="mb-2 list-inside list-disc space-y-1"
              >
                {children}
              </Text>
            ),
            // oxlint-disable-next-line react/no-unstable-nested-components
            ol: ({ children }) => (
              <Text
                component="ol"
                size="sm"
                className="mb-2 list-inside list-decimal space-y-1"
              >
                {children}
              </Text>
            ),
            // oxlint-disable-next-line react/no-unstable-nested-components
            li: ({ children }) => (
              <Text
                component="li"
                size="sm"
              >
                {children}
              </Text>
            ),
            // oxlint-disable-next-line react/no-unstable-nested-components
            blockquote: ({ children }) => (
              <Text
                component="blockquote"
                size="sm"
                fs="italic"
                className="my-2 border-l-4 border-gray-300 pl-3 opacity-75 dark:border-gray-600"
              >
                {children}
              </Text>
            ),
          }}
        >
          {message.content}
        </ReactMarkdown>
        </div>
      </Collapse>
    </div>
  );
}
